import { jsx as _jsx } from "react/jsx-runtime";
import { _className, ComponentSync, MenuBuilder, ModuleFE_MouseInteractivity, mouseInteractivity_PopUp, openContent, SimpleListAdapter, stopPropagation, TS_Link, TS_Table } from '@nu-art/thunderstorm-frontend/index';
import './Component_ReferencesTable.scss';
import { Dialog_ArticleEditor, Dialog_BookEditor, Dialog_LoincEditor, Dialog_SnomedEditor, Dialog_UpToDateEditor } from './Dialogs/index.js';
import { WarningDialog } from '../../shared-dialogs/index.js';
import { ModuleFE_Reference, resolveReferenceDisplay } from '../../../_entity.js';
export class Component_ReferencesTable extends ComponentSync {
    __onReferencesUpdated = (...params) => {
        if (['update', 'create'].includes(params[0]))
            this.reDeriveState();
    };
    // ######################## Life Cycle ########################
    deriveStateFromProps(nextProps) {
        const state = {};
        state.references = [];
        if (nextProps.referenceIds.length)
            state.references = ModuleFE_Reference.cache.filter(ref => nextProps.referenceIds.includes(ref._id));
        return state;
    }
    // ######################## Logic ########################
    /**
     * creates a right click menu with options for references
     * @param e - Event
     * @param onReferenceUpsertCallback - Callback to be called for reference upsert
     * @param onReferenceDeletedCallback - Callback to be called for reference delete
     * @param allowedRefs - Array of allowed ref types, if empty will allow all ref types
     * @param reference - reference to be edited
     */
    static handleReferencesRightClick = (e, onReferenceUpsertCallback, onReferenceDeletedCallback, allowedRefs, reference) => {
        stopPropagation(e);
        const menuItems = this.getAllowedAddRefs(allowedRefs, onReferenceUpsertCallback);
        if (reference) {
            menuItems.unshift({
                label: 'Edit Reference',
                onClick: () => {
                    switch (reference.type) {
                        case 'loinc':
                            Dialog_LoincEditor.show(onReferenceUpsertCallback, reference);
                            break;
                        case 'snomed':
                            Dialog_SnomedEditor.show(onReferenceUpsertCallback, reference);
                            break;
                        case 'book':
                            Dialog_BookEditor.show(onReferenceUpsertCallback, reference);
                            break;
                        case 'article':
                            Dialog_ArticleEditor.show(onReferenceUpsertCallback, reference);
                            break;
                        case 'upToDate':
                            Dialog_UpToDateEditor.show(onReferenceUpsertCallback, reference);
                    }
                }
            });
            menuItems.unshift({
                label: 'Delete Reference',
                onClick: () => {
                    WarningDialog.showDelete({
                        warningMessages: ['Are you sure you want to delete this reference?'],
                        title: 'Delete Reference',
                        executeButtonText: 'Delete Reference',
                        onConfirm: () => onReferenceDeletedCallback(reference._id)
                    });
                }
            });
        }
        const id = 'panel-values__right-click-menu';
        const adapter = SimpleListAdapter(menuItems, renderer => _jsx("div", { className: 'node-data', children: renderer.item.label }));
        const pos = { x: e.clientX, y: e.clientY };
        new MenuBuilder(adapter, pos, { x: 0, y: 1 })
            .setId(id)
            .setOnClick((path, item) => {
            ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
            item.onClick?.();
        })
            .setOffset({ x: 0, y: 10 })
            .show();
    };
    static getAllowedAddRefs = (allowedRefs, onReferenceUpsertCallback) => {
        const menuItems = [];
        if (allowedRefs === undefined)
            return menuItems;
        const allowAll = allowedRefs.length === 0;
        if (allowAll || allowedRefs.includes('snomed'))
            menuItems.push({
                label: 'Add Snomed Reference',
                onClick: () => Dialog_SnomedEditor.show(onReferenceUpsertCallback)
            });
        if (allowAll || allowedRefs.includes('loinc'))
            menuItems.push({
                label: 'Add Loinc Reference',
                onClick: () => Dialog_LoincEditor.show(onReferenceUpsertCallback)
            });
        if (allowAll || allowedRefs.includes('book'))
            menuItems.push({
                label: 'Add Book Reference',
                onClick: () => Dialog_BookEditor.show(onReferenceUpsertCallback)
            });
        if (allowAll || allowedRefs.includes('article'))
            menuItems.push({
                label: 'Add Article Reference',
                onClick: () => Dialog_ArticleEditor.show(onReferenceUpsertCallback)
            });
        if (allowAll || allowedRefs.includes('upToDate'))
            menuItems.push({
                label: 'Add UpToDate Reference',
                onClick: () => Dialog_UpToDateEditor.show(onReferenceUpsertCallback)
            });
        return menuItems;
    };
    // ######################## Render ########################
    renderDataCell = (ref) => {
        switch (ref.type) {
            case 'book': {
                const bookRef = ref;
                return _jsx("div", { className: 'td-data unselectable', children: resolveReferenceDisplay(bookRef) });
            }
            case 'article': {
                const link = ref;
                return _jsx("div", { className: 'td-data', children: _jsx(TS_Link, { url: link.data.url, target: '_blank', children: resolveReferenceDisplay(link) }) });
            }
            case 'upToDate': {
                const upToDate = ref;
                return _jsx("div", { className: 'td-data', children: _jsx(TS_Link, { url: upToDate.data.url, target: '_blank', children: resolveReferenceDisplay(upToDate) }) });
            }
            default: {
                return _jsx("div", { className: 'td-data unselectable', children: resolveReferenceDisplay(ref) });
            }
        }
    };
    render() {
        return _jsx("div", { className: 'references-list', children: _jsx(TS_Table, { rows: this.state.references || [], header: [{ header: 'type', widthPx: 100 }, 'data'], headerRenderer: columnKey => {
                    return _jsx("div", { className: 'th-data unselectable', children: columnKey[0].toUpperCase() + columnKey.slice(1) });
                }, cellRenderer: (prop, item, index) => {
                    if (prop === 'type')
                        return _jsx("div", { className: 'td-data unselectable', children: item.type });
                    if (prop === 'data') {
                        return this.renderDataCell(item);
                    }
                }, tr: (row, rowIndex) => ({
                    className: _className(row?.creationStatus === 'failed' ? 'invalid-reference' : '', this.props.blockEdit ? 'edit-blocked' : undefined),
                    ...(row?.creationStatus === 'failed' ? openContent.tooltip.bottom('invalid-ref-tool-tip', () => _jsx("div", { children: "Invalid Reference" })) : {}),
                    onContextMenu: async (e) => {
                        if (this.props.blockEdit)
                            return;
                        stopPropagation(e);
                        e.persist();
                        return Component_ReferencesTable.handleReferencesRightClick(e, this.props.onReferenceUpsertCallback, this.props.onReferenceDeletedCallback, this.props.allowedRefs, row);
                    }
                }) }) });
    }
}
//# sourceMappingURL=Component_ReferencesTable.js.map